import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeleteAccountRequest, UpdateProfileRequest, User } from '@financial/shared';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { fetchMe } from '../auth/api';
import { notify } from '../../store/notifications';

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    // El perfil de lectura es el mismo que valida la sesión. Mantener un único
    // endpoint evita que Ajustes se quede vacío cuando la API disponible sólo
    // expone /auth/me (por ejemplo, tras una actualización parcial).
    queryFn: fetchMe,
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: async (body: UpdateProfileRequest): Promise<User> => {
      const res = await api.patch<User>('/users/me', body);
      return res.data;
    },
    onSuccess: (user) => {
      qc.setQueryData(['profile'], user);
      setUser({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        defaultCurrency: user.defaultCurrency,
        aiConsentGranted: user.aiConsentGranted,
      });
      void qc.invalidateQueries({ queryKey: ['billing', 'entitlement'] });
      if (!user.aiConsentGranted) {
        void qc.cancelQueries({ queryKey: ['assistant'] }).finally(() => {
          qc.removeQueries({ queryKey: ['assistant'] });
        });
      }
      // El presupuesto/insights dependen del % de ahorro objetivo.
      void qc.invalidateQueries({ queryKey: ['budget'] });
      void qc.invalidateQueries({ queryKey: ['insights'] });
      notify.success('Ajustes guardados');
    },
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: async (body: DeleteAccountRequest): Promise<void> => {
      await api.delete('/users/me', { data: body });
    },
  });
}
